import type { ButtonHTMLAttributes } from 'react';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  icon: IconName;
  /** Required, not optional — DESIGN.md §7: "An icon-only button always has an aria-label and a
   * tooltip with the same words." */
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  /** MVP-268: overrides the glyph's pixel size independent of `size` (the button's box/hit-area).
   * `size` alone controls box AND icon together, which is right almost everywhere — but the guest
   * page needs a bigger box to clear DESIGN.md §6.3's 44px floor without the icon growing with it.
   * A bigger box reads as "easier to hit"; a bigger icon at the same distance reads as a heavier,
   * more prominent control nobody asked for — the two must be settable independently. */
  iconSize?: number;
  loading?: boolean;
  active?: boolean;
  /** MVP-438: express `disabled` as `aria-disabled` on a still-focusable button instead of the
   * native attribute. For a stepper this is the difference between "you have reached the end" and
   * "your keyboard focus has been thrown to `<body>`": the button a keyboard user just pressed is
   * the button that disables itself at the bound, and a disabled element cannot hold focus, so the
   * next Tab resumes from the top of the document several stops earlier.
   *
   * The click is swallowed here rather than left to the caller, so a soft-disabled button is inert
   * in exactly the way the native attribute would have made it. Not the default: a soft-disabled
   * control is still in the tab order, which is right for one button of a group the user is
   * working inside and wrong for, say, a submit button on an incomplete form. */
  keepFocusWhenDisabled?: boolean;
}

const ICON_SIZE: Record<NonNullable<IconButtonProps['size']>, number> = { sm: 16, md: 20, lg: 24 };

/** DESIGN.md §9.1 `IconButton`. Square, icon-only sibling of `Button` — same variant vocabulary,
 * same states, `title` doubles as the native tooltip so the accessible name and the visible
 * tooltip are always the same words. */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  iconSize,
  loading = false,
  active = false,
  className,
  disabled,
  keepFocusWhenDisabled = false,
  onClick,
  ...rest
}: IconButtonProps) {
  const classes = ['icon-btn', `icon-btn--${variant}`, `icon-btn--${size}`, active ? 'icon-btn--active' : '', className]
    .filter(Boolean)
    .join(' ');
  const glyphSize = iconSize ?? ICON_SIZE[size];
  // MVP-379: §7's tooltip is for a control the operator can actually point at. On a disabled button
  // the `title` is unreachable three ways — no focus, no hover on a touchscreen, and the browser
  // suppresses the tooltip on a disabled control — so leaving it there mostly reassures whoever
  // writes the next disabled state that the reason is "stated" when nobody can read it. The
  // accessible name stays either way; a caller that needs to explain a dark control renders words
  // (`.operator-console__transport-reason`, `SegmentedControl`'s `disabledReason`).
  const isDisabled = disabled || loading;
  // `loading` is never soft: a busy button must not be re-pressable, and a spinner is a state the
  // user is waiting out rather than a boundary they have walked into.
  const soft = keepFocusWhenDisabled && isDisabled && !loading;
  return (
    <button
      type="button"
      className={classes}
      aria-label={label}
      // MVP-448: a SOFT-disabled button keeps its tooltip. The rule above is about a control the
      // operator cannot reach; a soft-disabled one is focusable, hoverable and not `disabled` to the
      // browser, so the tooltip renders — and it is the button most in need of one, since the user
      // has just pressed it and is looking for why nothing happened. Dropping it here was MVP-379
      // applying the right rule to the wrong case.
      title={isDisabled && !soft ? undefined : label}
      aria-pressed={active || undefined}
      disabled={soft ? undefined : isDisabled}
      aria-disabled={soft || undefined}
      onClick={soft ? undefined : onClick}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={glyphSize - 4} /> : <Icon name={icon} size={glyphSize} />}
    </button>
  );
}
